import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { MergeType as ConsolidatedIcon, ShoppingCart as ShoppingCartIcon } from "@mui/icons-material";
import { SectionTitle } from "./StyledComponents";
import { useSmartShoppingList } from "../../../../../../hooks/useSmartShoppingList";
import { SHOPPING_CATEGORIES } from "../../../../../../data/ShoppingCategories";

/**
 * MealPlanShoppingList component with smart ingredient consolidation
 * Uses the smart shopping list API to get grocery store rounded quantities
 *
 * @param {string} organizationId - Organization ID
 * @param {string} mealPlanId - Meal plan ID
 * @param {Array} ingredients - Fallback list of ingredients from recipes (if API unavailable)
 */
const MealPlanShoppingList = ({ organizationId, mealPlanId, ingredients }) => {
  // Fetch smart shopping list from API
  const { data: shoppingListData, isLoading, error } = useSmartShoppingList(
    organizationId,
    mealPlanId,
  );

  // Use API data if available, otherwise fall back to local calculation
  const groupedIngredients = React.useMemo(() => {
    if (shoppingListData?.groupedByCategory) {
      // Use grouped data from API (categories are already proper case from API)
      return shoppingListData.groupedByCategory;
    }

    // Fallback: group local ingredients if API data not available
    if (!ingredients || ingredients.length === 0) {
      return {};
    }

    const grouped = ingredients.reduce((acc, ingredient) => {
      const category = ingredient.category || "Other";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(ingredient);
      return acc;
    }, {});

    return grouped;
  }, [shoppingListData, ingredients]);

  const shoppingList = shoppingListData?.shoppingList || ingredients || [];

  if (isLoading) {
    return (
      <Box sx={{ mb: 3 }}>
        <SectionTitle variant="h5">
          <ShoppingCartIcon />
          Smart Shopping List
        </SectionTitle>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            p: 3,
            mt: 2,
            textAlign: "center",
            backgroundColor: "background.paper",
          }}
        >
          <CircularProgress size={24} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Loading smart shopping list...
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    console.warn("Failed to load smart shopping list, using fallback:", error);
  }

  if (!shoppingList || shoppingList.length === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        <SectionTitle variant="h5">
          <ShoppingCartIcon />
          Smart Shopping List
        </SectionTitle>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            p: 3,
            mt: 2,
            textAlign: "center",
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No ingredients added to the shopping list yet
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle variant="h5">
        <ShoppingCartIcon />
        Smart Shopping List
        <Tooltip title="Ingredients are automatically consolidated and converted to practical shopping units, organized by shopping category">
          <ConsolidatedIcon
            sx={{ ml: 1, fontSize: 20, color: "primary.main" }}
          />
        </Tooltip>
      </SectionTitle>

      {Object.entries(groupedIngredients).map(
        ([categoryKey, categoryIngredients]) => (
          <Box key={categoryKey} sx={{ mt: 2 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 1,
                color: "primary.main",
                fontWeight: 600,
                fontSize: "1.1rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {SHOPPING_CATEGORIES[categoryKey?.toLowerCase()] || categoryKey}
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 2,
                mb: 1,
                backgroundColor: "background.paper",
              }}
            >
              <List disablePadding>
                {categoryIngredients.map((ingredient, index) => (
                  <React.Fragment key={ingredient.id}>
                    <ListItem sx={{ py: 1.5 }}>
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                width: "25%",
                                fontWeight: "500",
                                color: "text.secondary",
                              }}
                            >
                              {ingredient.amount} {ingredient.unit}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Typography variant="body1">
                                  {ingredient.name}
                                </Typography>
                                {ingredient.consolidated && (
                                  <Tooltip
                                    title={
                                      <div>
                                        <div>
                                          <strong>Consolidated from:</strong>
                                        </div>
                                        {ingredient.originalEntries?.map(
                                          (entry, idx) => (
                                            <div key={idx}>• {entry}</div>
                                          ),
                                        )}
                                        <div style={{ marginTop: 8 }}>
                                          <strong>Used in recipes:</strong>
                                          <div>
                                            {ingredient.sourceRecipes?.join(
                                              ", ",
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    }
                                  >
                                    <Chip
                                      icon={<ConsolidatedIcon />}
                                      label="Consolidated"
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      sx={{ fontSize: "0.7rem", height: 20 }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                              {ingredient.sourceRecipes &&
                                ingredient.sourceRecipes.length > 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: "block", mt: 0.5 }}
                                  >
                                    Used in:{" "}
                                    {ingredient.sourceRecipes.join(", ")}
                                  </Typography>
                                )}
                              {ingredient.originalAmount && ingredient.originalUnit && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.5, fontStyle: "italic" }}
                                >
                                  Original: {ingredient.originalAmount} {ingredient.originalUnit}
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        }
                      />
                    </ListItem>
                    {index < categoryIngredients.length - 1 && (
                      <Divider component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          </Box>
        ),
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mt: 2,
          fontStyle: "italic",
          textAlign: "center",
        }}
      >
        💡 Ingredients are automatically converted to grocery store package sizes
        (e.g., eggs rounded to dozens, milk to gallons/quarts). Quantities include
        all days of the meal plan.
      </Typography>
    </Box>
  );
};

export default MealPlanShoppingList;
